import { flushSync } from 'react-dom'

import { EMPTY_OBJECT } from '../../../../constants/common'
import { QA_NAME_CONFIRMATION_DUPLICATE_OPEN } from '../../../../constants/qa'
import { useMinimizedModalsContext } from '../../../../hooks'

/**
 * Wraps a modal-opening callback so that when a minimized instance for the same
 * `minimizeKey` already exists we show a confirmation with:
 *   - Cancel → keep the existing modal minimized
 *   - Submit → discard the existing minimized modal and open a new one
 *
 * If no minimized instance exists the wrapped callback runs as usual.
 */
export function useMinimizableModalOpener({
  confirmationProps = EMPTY_OBJECT,
  minimizeKey,
  onOpen,
} = EMPTY_OBJECT) {
  const context = useMinimizedModalsContext()

  function openModal(...args) {
    if (!context || !minimizeKey || !context.hasEntry(minimizeKey)) {
      onOpen?.(...args)

      return
    }

    const entry = context.getEntry(minimizeKey)

    const {
      cancelText = 'Modal.CancelBtnKey',
      description = 'Modal.MinimizedItemDuplicateOpenDescriptionKey',
      qaName = QA_NAME_CONFIRMATION_DUPLICATE_OPEN,
      submitText = 'Modal.ContinueBtnKey',
      title,
    } = confirmationProps

    context.showConfirmation({
      actionButtonsProps: {
        cancelText,
        submitText,
      },
      description,
      qaName,
      title,
      onCancel: () => {
        entry?.onConfirmationCancel?.()
      },
      onSubmit: () => {
        // Force the close (unregister + consumer's `setModal(null)`) to
        // commit before reopening, otherwise React batches both updates
        // into a single render and reuses the existing modal fiber, which
        // leaks the previous (minimized) state into the freshly opened one.
        flushSync(() => {
          entry?.onForceClose?.()
        })
        onOpen?.(...args)
      },
    })
  }

  return { openModal }
}
